import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import type { ScriptableContext } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

type MonthlyData = { debt: number; collected: number }
type TotalEwaDisbursementPastChartProps = {
  className?: string
  data: Record<string, MonthlyData>
}

export const TotalEwaDisbursementPastChart = ({ className, data }: TotalEwaDisbursementPastChartProps) => {
  const { __, __n } = useTranslator()
  const [searchParams] = useSearchParams()
  const monthParam = searchParams.get('month')
  const activeMonthIndex = monthParam ? dayjs(monthParam).month() : undefined
  const currentMonthIndex = dayjs().month()

  const labels = Array.from({ length: 12 }, (_, i) => `${i + 1}`)

  const debt = Array.from({ length: 12 }, (_, m) => {
    const key = `${String(m + 1).padStart(2, '0')}`
    const keyDebt = `${String(m + 1)}`

    return data?.[key]?.debt ?? data?.[keyDebt]?.debt ?? 0
  })

  const collected = Array.from({ length: 12 }, (_, m) => {
    const key = `${String(m + 1).padStart(2, '0')}`
    const keyCollected = `${String(m + 1)}`

    // if (m === currentMonthIndex) {
    //   return 0
    // }

    return data?.[key]?.collected ?? data?.[keyCollected]?.collected ?? 0
  })

  const backgroundDebt = debt.map((_, i) => {
    if (i === activeMonthIndex) return '#7EDDA9'
    if (i === currentMonthIndex) return 'rgba(40, 199, 111, 0.00)'

    return '#28C76F29'
  })

  const borderDebt = debt.map((_, i) => (i === currentMonthIndex ? 'rgba(40, 199, 111, 0.45)' : 'rgba(0,0,0,0)'))
  const borderWidthDebt = debt.map((_, i) => (i === currentMonthIndex ? 2 : 0))

  const backgroundCollected = collected.map((_, i) => {
    if (i === activeMonthIndex) return '#28C76F'
    if (i === currentMonthIndex) return 'rgba(40, 199, 111, 0.00)'

    // For other past months, collected blends into debt background; keep transparent so primary color is debt
    return 'rgba(40, 199, 111, 0.35)'
  })

  return (
    <div className={className}>
      <Bar
        data={{
          labels,
          datasets: [
            {
              label: __('Collected'),
              data: collected,
              backgroundColor: backgroundCollected,
              borderColor: borderDebt,
              borderWidth: borderWidthDebt,
              borderRadius: (ctx: ScriptableContext<'bar'>) => {
                const index = ctx.dataIndex
                const hasDebt = debt[index] > 0
                const hasCollected = collected[index] > 0
                const top = hasDebt ? 0 : 8
                const bottom = hasCollected ? 8 : 0

                return { topLeft: top, topRight: top, bottomLeft: bottom, bottomRight: bottom }
              },
              borderSkipped: false,
              barThickness: 36,
              stack: 'ewa'
            },
            {
              label: __('Debt'),
              data: debt,
              backgroundColor: backgroundDebt,
              borderColor: borderDebt,
              borderWidth: borderWidthDebt,
              borderRadius: (ctx: ScriptableContext<'bar'>) => {
                const index = ctx.dataIndex
                const hasCollected = collected[index] > 0
                const bottom = hasCollected ? 0 : 8
                const top = debt[index] > 0 ? 8 : 0

                return { topLeft: top, topRight: top, bottomLeft: bottom, bottomRight: bottom }
              },
              borderSkipped: false,
              barThickness: 36,
              stack: 'ewa'
            }
          ]
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1E293B',
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              displayColors: false,
              padding: 8,
              cornerRadius: 8,
              callbacks: {
                title: items => {
                  const idx = items?.[0]?.dataIndex ?? 0
                  const monthName = dayjs().month(idx).format('MMMM')

                  return monthName
                },
                label: context => {
                  const idx = context.dataIndex
                  const dsLabel = context.dataset.label || ''

                  let value = Number((context.parsed as any)?.y ?? 0)

                  if (dsLabel === __('Debt')) {
                    value = debt[idx]
                  }

                  return `${dsLabel}: ${__n(value)} đ`
                }
              }
            }
          },
          // Only show the hovered segment
          interaction: { mode: 'nearest', intersect: true },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              border: { display: false },
              ticks: { color: '#C2C4CC', font: { size: 12 } }
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.06)', display: false },
              border: { display: false },
              ticks: { display: false }
            }
          },
          layout: { padding: { top: 8 } }
        }}
      />
    </div>
  )
}

export default TotalEwaDisbursementPastChart
